import { api } from '@/helpers/api'
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx'
import { userService } from '../userService'
import { uiController } from '@/controllers/uiController'
import { TreeDataNode } from 'antd'
import { MenuKeyEnum, NotDefinedEnum } from '@/helpers/commonEnum'

type TreeDataType = TreeDataNode & { count?: number; parentKey: number }

class FolderService {
  @observable folderList: ServerData.Folder.ListData[] = []
  @observable isShowTree = false
  @observable isLoading = false
  @observable menuFileCount: Record<MenuKeyEnum, number> = {
    review: 0,
    materialLibrary: 0,
  }
  firstLoading = true

  constructor() {
    makeObservable(this)
  }

  @computed get folderOptions() {
    const data: Array<{ label: string; value: number }> = []

    const flatMap = (list: ServerData.Folder.ListData[]) => {
      list.forEach((folder) => {
        data.push({
          label: folder.folderName,
          value: folder.folderId,
        })

        if (folder.children) {
          flatMap(folder.children)
        }
      })
    }

    flatMap(this.folderList)
    return data
  }

  @computed get folderTreeData() {
    const convertToTreeData = (list: ServerData.Folder.ListData[]) => {
      return list.map((item: ServerData.Folder.ListData): TreeDataType => {
        return {
          key: item.folderId,
          parentKey: item.parentId,
          title: item.folderName,
          count: item.count,
          children: item.children ? convertToTreeData(item.children) : [],
        }
      })
    }

    return convertToTreeData(this.folderList)
  }

  /**
   * 过滤掉未分类
   */
  @computed get folderTreeDataNo() {
    return this.folderTreeData.filter((item) => item.key !== NotDefinedEnum.NOT_FOLDER)
  }

  /**
   * 过滤掉未分类
   */
  @computed get folderOptionsNo() {
    return this.folderOptions.filter((item) => item.value !== NotDefinedEnum.NOT_FOLDER)
  }

  public init() {
    runInAction(() => {
      if (userService.menuKey.type === 'Tree' && userService.menuKey.key) {
        this.isShowTree = true
      }
    })

    autorun(() => {
      if (userService.workspaceId) {
        this.refresh()
      }
    })
  }

  public refresh() {
    this.getFolderList()
    this.getMenuFileCount()
  }

  @action
  public async getFolderList() {
    if (this.firstLoading) {
      this.isLoading = true
    }
    try {
      const res = await api.folder.list({
        workspaceId: userService.workspaceId,
      })
      if (res.success) {
        this.folderList = res.data
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
      this.firstLoading = false
    }
  }

  @action
  public async addFolder(name: string, parentId?: string) {
    const res = await api.folder.add({
      folderName: name,
      workspaceId: userService.workspaceId,
      parentId,
    })
    if (res.success) {
      this.getFolderList()
      uiController.message.success('添加文件夹成功')
      return res.data
    }
  }

  @action
  public async updateFolder(folderId: string, name: string) {
    const res = await api.folder.update({
      folderId,
      folderName: name,
      workspaceId: userService.workspaceId,
    })
    if (res.success) {
      this.getFolderList()
      uiController.message.success('修改成功')
    }
  }

  @action
  public async deleteFolder(folderId: string) {
    const res = await api.folder.del({
      folderId,
      workspaceId: userService.workspaceId,
    })
    if (res.success) {
      this.getFolderList()
      uiController.message.success('删除成功')
    }
  }

  @action
  public async moveFolder(folderId: string, parentId: string) {
    const res = await api.folder.move({
      folderId,
      parentId,
      workspaceId: userService.workspaceId,
    })
    if (res.success) {
      this.getFolderList()
      uiController.message.success('移动文件夹成功')
    }
  }

  @action
  public setIsShowTree(isShowTree: boolean) {
    this.isShowTree = isShowTree
  }

  @action
  public async getMenuFileCount() {
    const res = await api.material.sum({ workspaceId: userService.workspaceId })
    if (res.success) {
      this.menuFileCount.review = res.data.approvedCount
      this.menuFileCount.materialLibrary = res.data.reviewCount
    }
  }
}

export const folderService = new FolderService()
